import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { DomSanitizer } from '@angular/platform-browser';
import { AppConstants } from '../constants/constants';
import { ActivityLogsResponse, User } from 'src/app/core/user/user.types';
import { UserState } from './state/user.state';
import { BusinessAccountService } from 'src/app/pages/business-account/services/business-account.service';
import { PermissionService } from '../services/permission.service';
import { ModuleKey, ModuleStatus } from '../constants/module-status.enum';

@Injectable({
	providedIn: 'root'
})
export class UserService {
	private readonly _allowedImageMimeTypes = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/svg+xml']);
	private readonly _maxImageBlobSizeBytes = 10 * 1024 * 1024;

	constructor(private readonly _httpClient: HttpClient,
		private readonly _sanitizer: DomSanitizer,
		private readonly _businessAccountService: BusinessAccountService,
		private readonly _permissionService: PermissionService) { }

	/**
	 * Get the current logged in user data
	 */
	get(skipBusinessCalls: boolean = false): Observable<User> {
		const url = AppConstants.API.USERS_API;
		return this._httpClient.get<User>(url).pipe(
			tap((user: any) => {
				let currentUser: User = {
					...user,
					firstName: user.fullName.split(" ")[0],
				}

				// Store permissions
				let pessmision: string[] = [];
				currentUser.permissions.forEach((permissionGroup: any) => {
					permissionGroup.permissions.forEach((singlePermission: any) => {
						if (singlePermission.enabled) {
							pessmision.push(singlePermission.value)
						}
					})
				});
				localStorage.setItem(AppConstants.PRISISTED_KEYS.USER_PERMISSIONS, JSON.stringify(pessmision));

				// Store module statuses if user has managed accounts
				if (user?.manageAccounts?.length) {
					const account = user.manageAccounts[0];

					// Build module status map using type-safe enums
					const moduleStatusMap: Record<string, ModuleStatus> = {
						[ModuleKey.AI_ASSISTANT]: account.aiAssistantStatus as ModuleStatus,
						[ModuleKey.RECONCILIATION]: account.reconciliationModuleAccessStatus as ModuleStatus,
						[ModuleKey.MERCHANT_RECONCILIATION]: account.merchantReconciliationModuleAccessStatus as ModuleStatus,
						[ModuleKey.OPEN_BANKING]: account.openBankingModuleAccessStatus as ModuleStatus,
						[ModuleKey.BOOK_RECONCILIATION]: account.booKReconciliationModuleAccessStatus as ModuleStatus,
						[ModuleKey.PAYMENTS]: account.paymentModuleAccessStatus as ModuleStatus,
						[ModuleKey.MANUAL_BANK_STATEMENT]: account.manualBankStatementStatus as ModuleStatus,
						[ModuleKey.FORECASTING]: account.forecastingModuleAccessStatus as ModuleStatus,
						[ModuleKey.CASH_POSITIONING]: account.cashPositioningStatus as ModuleStatus,
					};

					// Use PermissionService to centralize module status management
					this._permissionService.setModuleStatuses(moduleStatusMap);

					// Conditionally fire business account calls only if skipBusinessCalls is false
					if (!skipBusinessCalls) {
						this._businessAccountService.getBusinessAccount(user?.manageAccounts[0]?.id).subscribe();
						this._businessAccountService.getCompanyPhoto(user.manageAccounts[0].id).subscribe();
					}
				}

				UserState.setUserState(currentUser);
			})
		);
	}

	/**
	 * Get the current logged in user photo
	 */
	getProfilePhoto(): Observable<any> {
		const url = `${AppConstants.API.PROFILE_API}/photo`;;
		return this._httpClient.get(url, { responseType: 'blob' }).pipe(
			tap((img: Blob) => {
				if (!this._isSafeImageBlob(img)) {
					return;
				}
				let objectURL = globalThis.URL.createObjectURL(img);
				// Security Review: SAFE - objectURL from createObjectURL (blob: protocol), data from internal API
				let userPhoto = this._sanitizer.bypassSecurityTrustUrl(objectURL); // NOSONAR typescript:S6268
				UserState.setUserPhotoState(userPhoto);
			})
		);
	}

	private _isSafeImageBlob(blob: Blob): boolean {
		return blob.size > 0 &&
			blob.size <= this._maxImageBlobSizeBytes &&
			this._allowedImageMimeTypes.has(blob.type);
	}

	/**
	 * get Current User ActivityLogs
	 */
	getCurrentUserActivityLogs(page = 0, size = 10): Observable<ActivityLogsResponse> {
		const url = `${AppConstants.API.PROFILE_API}/activityLog?page=${page}&size=${size}`;
		return this._httpClient.get<ActivityLogsResponse>(url);
	}
}
